import * as React from 'react';

import { cn, ThemeColor } from '@rematter/ui-web';
import { Handle, NodeProps } from '@xyflow/react';
import { useAtomValue } from 'jotai';

import {
  $errorCountByNodeId,
  $warningCountByNodeId,
} from '../../../_state/flowValidation.state';

import type {
  AutomationNode,
  HandleConfig,
  NodeVisualDefinition,
} from './types';

type BaseNodeProps = NodeProps<AutomationNode> & {
  definition: NodeVisualDefinition;
  className?: string;
  footer?: React.ReactNode;
};

/**
 * Consolidated color class mappings for all ThemeColors
 */
const colorClasses: Record<
  ThemeColor,
  { bg: string; text: string; border: string; ring: string }
> = {
  neutral: {
    bg: 'bg-neutral/15',
    text: 'text-neutral',
    border: 'border-neutral',
    ring: 'ring-neutral/30',
  },
  white: {
    bg: 'bg-white/15',
    text: 'text-white',
    border: 'border-white',
    ring: 'ring-white/30',
  },
  primary: {
    bg: 'bg-primary/15',
    text: 'text-primary',
    border: 'border-primary',
    ring: 'ring-primary/30',
  },
  success: {
    bg: 'bg-success/15',
    text: 'text-success',
    border: 'border-success',
    ring: 'ring-success/30',
  },
  failure: {
    bg: 'bg-failure/15',
    text: 'text-failure',
    border: 'border-failure',
    ring: 'ring-failure/30',
  },
  warning: {
    bg: 'bg-warning/15',
    text: 'text-warning',
    border: 'border-warning',
    ring: 'ring-warning/30',
  },
  brown: {
    bg: 'bg-brown/15',
    text: 'text-brown',
    border: 'border-brown',
    ring: 'ring-brown/30',
  },
  rust: {
    bg: 'bg-rust/15',
    text: 'text-rust',
    border: 'border-rust',
    ring: 'ring-rust/30',
  },
  magenta: {
    bg: 'bg-magenta/15',
    text: 'text-magenta',
    border: 'border-magenta',
    ring: 'ring-magenta/30',
  },
  purple: {
    bg: 'bg-purple/15',
    text: 'text-purple',
    border: 'border-purple',
    ring: 'ring-purple/30',
  },
  slate: {
    bg: 'bg-slate/15',
    text: 'text-slate',
    border: 'border-slate',
    ring: 'ring-slate/30',
  },
  teal: {
    bg: 'bg-teal/15',
    text: 'text-teal',
    border: 'border-teal',
    ring: 'ring-teal/30',
  },
  olive: {
    bg: 'bg-olive/15',
    text: 'text-olive',
    border: 'border-olive',
    ring: 'ring-olive/30',
  },
  transparent: {
    bg: 'bg-transparent',
    text: 'text-foreground',
    border: 'border-transparent',
    ring: 'ring-transparent',
  },
};

export const getColorClasses = (color: ThemeColor) =>
  colorClasses[color] ?? colorClasses.primary;

/**
 * Shared base node component that renders the standard node layout
 * with handles, icon, label, and preview text based on the definition.
 */
export const BaseNode: React.FC<BaseNodeProps> = ({
  id,
  data,
  selected,
  definition,
  className,
  footer,
}) => {
  // Read validation counts from Jotai using node id
  const errorCounts = useAtomValue($errorCountByNodeId);
  const warningCounts = useAtomValue($warningCountByNodeId);
  const errorCount = errorCounts.get(id) ?? 0;
  const warningCount = warningCounts.get(id) ?? 0;

  const Icon = definition.icon;
  const config = data.config;
  const content = definition.getContent?.(config) || definition.description;

  const hasErrors = errorCount > 0;
  const hasWarnings = warningCount > 0;

  return (
    <div
      className={cn(
        '@BaseNode',
        'relative min-w-[220px] rounded-lg bg-white p-2 shadow-sm',
        'border transition-all duration-150',
        selected
          ? cn(
              getColorClasses(definition.color).border,
              'ring-2 ring-offset-1',
              getColorClasses(definition.color).ring,
            )
          : 'border-neutral/15',
        'hover:shadow-md',
        className,
      )}
    >
      {definition.handles.map((handle: HandleConfig, i: number) => (
        <Handle
          key={`${handle.type}-${handle.id || i}`}
          type={handle.type}
          position={handle.position}
          id={handle.id}
          className={cn('h-3! w-3! border-2! border-white!', handle.className)}
        />
      ))}

      {/* Error/Warning Badge */}
      {(hasErrors || hasWarnings) && (
        <div className="absolute -top-1.5 -right-1.5 z-10">
          <div
            className={cn(
              'flex size-5 items-center justify-center rounded-full text-xs font-semibold text-white shadow-sm',
              hasErrors ? 'bg-failure' : 'bg-warning',
            )}
          >
            {hasErrors ? errorCount : warningCount}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2 pb-2">
        <div
          className={cn(
            'flex size-5 items-center justify-center rounded-md',
            getColorClasses(definition.color).bg,
          )}
        >
          <Icon
            className={cn('size-3', getColorClasses(definition.color).text)}
          />
        </div>
        <span className="truncate text-sm font-medium text-neutral">
          {data.label || definition.label}
        </span>
      </div>

      <div className="h-px w-full bg-neutral/10" />

      {/* Content */}
      <p className="line-clamp-2 pt-2 text-xs leading-relaxed text-neutral/50">
        {content}
      </p>

      {/* Footer (optional) */}
      {footer && (
        <div className="border-t border-neutral/10 px-4 py-2">
          {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
          <div className="nodrag">{footer}</div>
        </div>
      )}
    </div>
  );
};
